import type { IPhoneNumber } from "../components/staff/PhoneNumber";
import type { IOfficeNew } from "../interfaces/models/IOffice";
import type {
  IContactInfo,
  IOfficeMainContact,
  IOfficeMainContactWrite,
} from "../interfaces/models/IOfficeMainContact";
import { formatOfficeTabLabel } from "../pages/ManageFirm/Professionals/manageDataAdminOfficeRules";

export type PersonForMainContact = {
  id?: string;
  name?: string;
  lastName?: string;
  firmEmail?: string;
  photo?: string;
  phoneNumber?: string | IPhoneNumber;
  countryCodePhoneNumber?: string;
};

function newContactRowId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `contact-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function formatPhoneDisplay(
  phone: string | IPhoneNumber | undefined,
  countryCode?: string,
): string {
  if (!phone) {
    const code = String(countryCode ?? "").trim();
    return code;
  }
  if (typeof phone === "string") {
    return phone.trim();
  }
  const code = String(phone.code ?? countryCode ?? "").trim();
  const num = String(phone.phone ?? "").trim();
  return [code, num].filter(Boolean).join(" ");
}

export function personToContactInfo(
  person: PersonForMainContact,
  officeId: string,
  existing?: Pick<
    IContactInfo,
    "id" | "createdDate" | "_ts" | "isDeleted"
  >,
): IContactInfo {
  const contactId = String(person.id ?? "").trim();
  const name = `${person.name ?? ""} ${person.lastName ?? ""}`.trim();
  const now = Date.now();
  return {
    id: existing?.id ?? newContactRowId(),
    createdDate: existing?.createdDate ?? now,
    ...(existing?._ts != null ? { _ts: existing._ts } : {}),
    ...(existing?.isDeleted != null ? { isDeleted: existing.isDeleted } : {}),
    contactId,
    officeId: String(officeId).trim(),
    name,
    photoUrl: String(person.photo ?? "").trim(),
    email: String(person.firmEmail ?? "").trim(),
    phone: formatPhoneDisplay(
      person.phoneNumber,
      person.countryCodePhoneNumber,
    ),
  };
}

export function officeMainContactBlockKey(
  contact: Pick<IOfficeMainContact, "id" | "firmId" | "officeId">,
): string {
  return String(
    contact.id?.trim() || `${contact.firmId}-${contact.officeId}`,
  );
}

function createEmptyOfficeMainContact(
  firmId: string,
  officeId: string,
): IOfficeMainContact {
  return {
    firmId,
    officeId,
    contacts: [],
  };
}

export function mergeOfficeMainContactsWithOffices(
  docs: IOfficeMainContact[],
  offices: IOfficeNew[],
  firmId: string,
): IOfficeMainContact[] {
  const trimmedFirmId = String(firmId ?? "").trim();
  if (!trimmedFirmId || offices.length === 0) {
    return docs;
  }

  const existingByOfficeId = new Map(
    docs.map((doc) => [String(doc.officeId ?? "").trim(), doc]),
  );

  return offices.map((office) => {
    const officeId = String(office.id ?? "").trim();
    const existing = existingByOfficeId.get(officeId);
    if (existing) {
      return existing;
    }
    return createEmptyOfficeMainContact(trimmedFirmId, officeId);
  });
}

export function officeLabelForMainContact(office: IOfficeNew): string {
  return formatOfficeTabLabel(office);
}

export function toOfficeMainContactWritePayload(
  view: IOfficeMainContact,
): IOfficeMainContactWrite {
  const officeId = String(view.officeId ?? "").trim();
  const contacts = (view.contacts ?? []).map((entry) => {
    const normalized: IContactInfo = {
      ...entry,
      officeId,
    };
    return normalized;
  });

  const payload: IOfficeMainContactWrite = {
    firmId: String(view.firmId ?? "").trim(),
    officeId,
    contacts,
  };

  const id = String(view.id ?? "").trim();
  if (id) {
    payload.id = id;
  }

  return payload;
}

export function serializeOfficeMainContacts(contacts: IContactInfo[]): string {
  return JSON.stringify(
    contacts.map((entry) => ({
      id: entry.id,
      contactId: entry.contactId,
      officeId: entry.officeId,
      createdDate: entry.createdDate,
      isDeleted: entry.isDeleted,
    })),
  );
}

export function validateOfficeMainContactList(
  contacts: IContactInfo[],
): string | null {
  const seen = new Set<string>();
  for (const entry of contacts) {
    const contactId = String(entry.contactId ?? "").trim();
    if (!contactId) {
      return "Each contact must reference a professional";
    }
    if (seen.has(contactId)) {
      return "Duplicate professional in the same office";
    }
    seen.add(contactId);
  }
  return null;
}
